import { Fragment } from 'react';
import { ProductLogo } from '@/components/ProductLogo';
import type { PlanningSection } from '@/lib/api/assessment';
import type { NormalizedPlanningView, PlanningReportMetaUi } from '@/lib/api/planning-info-mapper';
import {
  majorSectionTitleKeys,
  shouldShowBlockSubtitle,
} from '@/lib/planning-report-i18n';
import styles from './Doch33ReportView.module.css';

function formatMajorTitle(index: number, title: string): string {
  const trimmed = title.trim();
  if (/^\d+\./.test(trimmed)) {
    return trimmed;
  }
  return `${index}. ${trimmed}`;
}

function parseParcelMetaLine(line: string): { label?: string; value: string }[] {
  return line.split(' · ').map((part) => {
    const trimmed = part.trim();
    const colon = trimmed.indexOf(': ');
    if (colon > 0) {
      return {
        label: trimmed.slice(0, colon),
        value: trimmed.slice(colon + 2),
      };
    }
    return { value: trimmed };
  });
}

function renderCellValue(value: string) {
  if (!value || value === '—') {
    return <span className={styles.emptyCell}>—</span>;
  }
  return value;
}

type FieldLabels = {
  verifySource: string;
  openPdfOnPage: string;
  draftLabel: string;
};

function renderFields(section: PlanningSection, labels: FieldLabels) {
  if (!section.fields?.length) return null;

  return (
    <table className={styles.kvTable}>
      <tbody>
        {section.fields.map((field) => {
          const isVerified = field.verified === true;
          const pageRef = field.href?.includes('#page=')
            ? field.href.split('#page=')[1]
            : null;
          const showMeta =
            field.confidence != null || Boolean(field.href && pageRef);

          return (
            <tr key={`${field.label}-${field.value}`}>
              <th scope="row">{field.label}</th>
              <td>
                {field.href && !field.href.includes('#page=') ? (
                  <a
                    className={styles.fieldValueLink}
                    href={field.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {field.value}
                  </a>
                ) : (
                  <span className={styles.fieldValue}>{renderCellValue(field.value)}</span>
                )}
                {showMeta ? (
                  <div className={styles.fieldMeta}>
                    {field.confidence != null && isVerified ? (
                      <span className={styles.fieldConfidence}>
                        {Math.round(field.confidence * 100)}%
                      </span>
                    ) : null}
                    {field.confidence != null && !isVerified ? (
                      <span className={styles.fieldDraft}>{labels.draftLabel}</span>
                    ) : null}
                    {field.href && pageRef ? (
                      <a
                        className={styles.fieldSourceLink}
                        href={field.href}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {isVerified ? labels.verifySource : labels.openPdfOnPage} ({pageRef})
                      </a>
                    ) : null}
                  </div>
                ) : null}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function renderDataTable(section: PlanningSection) {
  if (!section.columns?.length || !section.rows?.length) return null;

  return (
    <div className={styles.tableWrap}>
      <table className={styles.dataTable}>
        <thead>
          <tr>
            {section.columns.map((col) => (
              <th key={col} scope="col">
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {section.rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {section.columns!.map((_col, cellIndex) => {
                const cell = row[cellIndex] ?? '—';
                return (
                  <td key={`${rowIndex}-${cellIndex}`}>
                    {renderCellValue(cell)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderItems(section: PlanningSection) {
  if (!section.items?.length) return null;

  return (
    <table className={styles.kvTable}>
      <tbody>
        {section.items.map((item) => (
          <tr key={`${item.label}-${item.value}`}>
            <th scope="row">{item.label}</th>
            <td>{renderCellValue(item.value)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function renderNotes(section: PlanningSection) {
  if (!section.notes?.length) return null;

  return (
    <ul className={styles.notesList}>
      {section.notes.map((note, index) => (
        <li key={index}>{note}</li>
      ))}
    </ul>
  );
}

function renderBlock(
  section: PlanningSection,
  testId: string,
  fieldLabels: FieldLabels,
  majorTitles: string[],
) {
  const hasSubtitle = shouldShowBlockSubtitle(section.title, majorTitles);

  return (
    <div className={styles.block} data-testid={testId}>
      {hasSubtitle ? <h3 className={styles.blockTitle}>{section.title}</h3> : null}
      {renderFields(section, fieldLabels)}
      {renderDataTable(section)}
      {renderItems(section)}
      {renderNotes(section)}
    </div>
  );
}

function renderMajorSection(
  index: number,
  title: string,
  blocks: PlanningSection[],
  majorTitles: string[],
  fieldLabels: FieldLabels,
  extra?: PlanningSection,
  testId?: string,
) {
  const allBlocks = extra ? [...blocks, extra] : blocks;
  if (!allBlocks.length) return null;

  return (
    <section className={styles.majorSection} data-testid={testId}>
      <div className={styles.majorHeader}>
        <span className={styles.majorIndex}>{index}</span>
        <h2 className={styles.majorTitle}>{formatMajorTitle(index, title)}</h2>
      </div>
      <div className={styles.majorBody}>
        {allBlocks.map((block, blockIndex) => (
          <Fragment key={`${testId}-block-${blockIndex}`}>
            {renderBlock(
              block,
              `${testId}-block-${blockIndex}`,
              fieldLabels,
              majorTitles,
            )}
          </Fragment>
        ))}
      </div>
    </section>
  );
}

function buildCompactMetaLine(parts: (string | undefined | null)[]): string {
  return parts.filter((p) => p?.trim()).join(' · ');
}

export type Doch33ReportViewProps = {
  view: NormalizedPlanningView;
  dir?: 'rtl' | 'ltr';
  variant?: 'screen' | 'print';
  coverBadge?: string;
  reportHeading: string;
  reportSubtitle?: string;
  reportMeta?: PlanningReportMetaUi;
  reportDateLabel: string;
  requestTypeLabel: string;
  parcelLine?: string;
  documentRefLabel?: string;
  documentRef?: string;
  informationalNotice?: string;
  producedByLine?: string;
  verifySourceLabel: string;
  openPdfOnPageLabel: string;
  extractionDraftLabel: string;
};

export default function Doch33ReportView({
  view,
  dir = 'rtl',
  variant = 'screen',
  coverBadge,
  reportHeading,
  reportSubtitle,
  reportMeta,
  reportDateLabel,
  requestTypeLabel,
  parcelLine,
  documentRefLabel,
  documentRef,
  informationalNotice,
  producedByLine,
  verifySourceLabel,
  openPdfOnPageLabel,
  extractionDraftLabel,
}: Doch33ReportViewProps) {
  const majorTitles = majorSectionTitleKeys(view);
  const fieldLabels: FieldLabels = {
    verifySource: verifySourceLabel,
    openPdfOnPage: openPdfOnPageLabel,
    draftLabel: extractionDraftLabel,
  };
  const isScreen = variant === 'screen';
  const parcelMeta = parcelLine ? parseParcelMetaLine(parcelLine) : [];
  const compactMeta = isScreen
    ? buildCompactMetaLine([
        parcelLine,
        reportMeta?.report_date
          ? `${reportDateLabel}: ${reportMeta.report_date}`
          : undefined,
      ])
    : '';

  return (
    <article
      id="doch33-report"
      className={`${styles.report} ${isScreen ? styles.reportScreen : styles.reportPrint}`}
      dir={dir}
      data-testid="doch33-report"
    >
      <header className={isScreen ? styles.coverScreen : styles.cover}>
        {isScreen ? (
          <>
            {compactMeta ? <p className={styles.coverLead}>{compactMeta}</p> : null}
            {informationalNotice ? (
              <p className={styles.coverNotice}>{informationalNotice}</p>
            ) : null}
          </>
        ) : (
          <>
            <div className={styles.coverTop}>
              <div className={styles.coverBrand} dir="ltr">
                <ProductLogo variant="full" compact />
              </div>
              {coverBadge ? <div className={styles.coverBadge}>{coverBadge}</div> : null}
            </div>

            <div className={styles.coverIntro}>
              <h1 className={styles.reportHeading}>{reportHeading}</h1>
              {reportSubtitle ? <p className={styles.reportSubtitle}>{reportSubtitle}</p> : null}
            </div>

            {parcelMeta.length > 0 ? (
              <div className={styles.metaParcelBand}>
                {parcelMeta.map((item) => (
                  <span
                    key={`${item.label ?? 'value'}-${item.value}`}
                    className={styles.metaChip}
                  >
                    {item.label ? (
                      <span className={styles.metaChipLabel}>{item.label}</span>
                    ) : null}
                    <span className={styles.metaChipValue}>{item.value}</span>
                  </span>
                ))}
              </div>
            ) : null}

            <dl className={styles.metaGrid}>
              {reportMeta?.report_date ? (
                <div className={styles.metaItem}>
                  <dt>{reportDateLabel}</dt>
                  <dd>
                    {reportMeta.report_date}
                    {reportMeta.hebrew_date ? (
                      <span className={styles.hebrewDate}>{reportMeta.hebrew_date}</span>
                    ) : null}
                  </dd>
                </div>
              ) : null}
              {reportMeta?.request_type ? (
                <div className={styles.metaItem}>
                  <dt>{requestTypeLabel}</dt>
                  <dd>{reportMeta.request_type}</dd>
                </div>
              ) : null}
              {documentRef && documentRefLabel ? (
                <div className={styles.metaItem}>
                  <dt>{documentRefLabel}</dt>
                  <dd className={styles.mono}>{documentRef}</dd>
                </div>
              ) : null}
            </dl>

            {informationalNotice ? (
              <aside className={styles.noticeStrip}>
                <span className={styles.noticeIcon} aria-hidden>
                  i
                </span>
                <p>{informationalNotice}</p>
              </aside>
            ) : null}
          </>
        )}
      </header>

      <div className={isScreen ? styles.bodyScreen : styles.body}>
        {view.section1Blocks.length > 0
          ? renderMajorSection(
              1,
              view.section1Title,
              view.section1Blocks,
              majorTitles,
              fieldLabels,
              undefined,
              'planning-section-1',
            )
          : null}
        {view.section2Blocks.length > 0
          ? renderMajorSection(
              2,
              view.section2Title,
              view.section2Blocks,
              majorTitles,
              fieldLabels,
              undefined,
              'planning-section-2',
            )
          : null}
        {view.section3Blocks.length > 0
          ? renderMajorSection(
              3,
              view.section3Title,
              view.section3Blocks,
              majorTitles,
              fieldLabels,
              undefined,
              'planning-section-3',
            )
          : null}
        {view.section4
          ? renderMajorSection(
              4,
              view.section4Title,
              [],
              majorTitles,
              fieldLabels,
              view.section4,
              'planning-section-4',
            )
          : null}
      </div>

      {producedByLine ? (
        <footer className={styles.reportFooter}>
          <p>{producedByLine}</p>
        </footer>
      ) : null}
    </article>
  );
}
